import React, { useCallback, useRef, useState } from 'react'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import clsx from 'clsx'
import { Spinner } from '../Spinner'
import styles from './styles.module.scss'

type SelectorProps<T> = {
    label?: string
    options: { label: React.ReactNode; value: T }[]
    value: string
    loading?: boolean
    handleSelect: (v: T) => void
    className?: string
    fallbackLabel?: React.ReactNode
}

export const Selector = <T,>({
    label,
    options,
    value,
    loading,
    handleSelect,
    className,
    fallbackLabel,
}: SelectorProps<T>) => {
    const [open, setOpen] = useState(false)

    const ref = useRef<HTMLButtonElement>(null)

    const close = useCallback(() => {
        setOpen(false)
    }, [])

    useOnClickOutside({ ref, callback: close })

    const title = options.find((x) => x.value === value)?.label ?? fallbackLabel

    return (
        <div className={clsx(styles.wrapper, className, loading && styles.loading)}>
            <button
                ref={ref}
                className={styles.selector}
                onClick={() => {
                    setOpen((v) => (loading ? false : !v))
                }}
            >
                <div>
                    <div className={clsx(styles.label, !title ? styles.big : '')}>
                        <Spinner className={styles.spinner} />
                        {label}
                    </div>
                    <div className={styles.title}>{title}</div>
                </div>

                <i className={styles.arrow} />
            </button>

            <div className={clsx(styles.menu, open && styles.open)}>
                {options.map((opt) => (
                    <button
                        key={String(opt.value)}
                        onClick={() => {
                            if (loading) return
                            handleSelect(opt.value)
                            close()
                        }}
                        className={opt.value === value ? styles.selected : ''}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    )
}
